// Admin "Attention" tab — the things Suresh wants to see at a glance
// (2026-09-25): pending review inbox, outgoing-loads freshness, sheet
// sync freshness, and customers whose current balance is running above
// their historical average. Data from /api/attention/summary (5-min
// server cache; admin-only).
import { useApi } from '../api.js';
import { Link } from 'react-router-dom';

const money = (n) => '$' + Math.round(n ?? 0).toLocaleString();

function ago(ts) {
  if (!ts) return '—';
  const ms = Date.now() - new Date(ts).getTime();
  if (!Number.isFinite(ms)) return String(ts);
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return mins + ' min ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return hrs + ' h ago';
  return Math.floor(hrs / 24) + ' days ago';
}
function staleColor(ts, warnHours, badHours) {
  if (!ts) return '#991b1b';
  const hrs = (Date.now() - new Date(ts).getTime()) / 3600000;
  if (hrs >= badHours) return '#991b1b';
  if (hrs >= warnHours) return '#c05100';
  return '#15803d';
}

function Card({ title, children, tone }) {
  const border = tone === 'bad' ? '#fca5a5' : tone === 'warn' ? '#fcd34d' : '#e2e8f0';
  const bg     = tone === 'bad' ? '#fef2f2' : tone === 'warn' ? '#fffbeb' : '#fff';
  return (
    <div className="card" style={{ border: '1px solid ' + border, background: bg, padding: '0.75rem 1rem', minWidth: 260, flex: '1 1 260px' }}>
      <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>{title}</div>
      {children}
    </div>
  );
}

export default function AdminAttention() {
  const { data, error, loading, refresh } = useApi('/api/attention/summary');
  if (loading) return <div className="loading">Checking what needs attention…</div>;
  if (error)   return <div className="error">Error: {error}</div>;
  if (!data)   return null;

  const pr  = data.pendingReview || {};
  const ol  = data.outgoingLoads || {};
  const ss  = data.sheetSync || {};
  const baRaw = data.balanceAlerts || { alerts: [] };
  // Robust against API/frontend deploy skew: recompute the under-%
  // client-side and keep only customers genuinely BELOW their average.
  const ba = {
    ...baRaw,
    alerts: (baRaw.alerts || [])
      .map(a => ({
        ...a,
        pctUnder: a.pctUnder != null
          ? a.pctUnder
          : (a.avgClosing > 0 ? Math.round((1 - (a.current / a.avgClosing)) * 100) : null)
      }))
      .filter(a => a.pctUnder != null && a.pctUnder > 0)
      .sort((x, y) => y.pctUnder - x.pctUnder)
  };
  const inv = data.invoicing || {};
  // Suresh 2026-09-29: groups with nothing to bill are noise -- drop
  // them from the table entirely (they come back the moment they have
  // an open balance or go overdue).
  const withStatus = (g) => g.status ? g : { ...g, status: g.ok ? 'on-track' : 'overdue', openBalance: null };
  const invWeekly = (inv.weekly || []).map(withStatus).filter(g => g.status !== 'nothing-to-bill');
  const invStmt = (inv.statement || []).map(withStatus).filter(g => g.status !== 'nothing-to-bill');
  const invNeg = inv.negativeBalances || [];
  const invMissed = inv.missedCount || 0;
  const alerts = ba.alerts || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>🔔 Needs attention</h2>
          <div className="small" style={{ color: '#666' }}>
            Snapshot from {ago(data.generatedAt)} — server refreshes every 5 minutes.
          </div>
        </div>
        <button className="refresh-btn" onClick={refresh}>Refresh</button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <Card title="📥 Pending review" tone={pr.count > 0 ? (pr.count >= 20 ? 'bad' : 'warn') : undefined}>
          <div style={{ fontSize: '1.6em', fontWeight: 700 }}>{pr.count ?? 0}</div>
          <div className="small" style={{ color: '#666' }}>
            {pr.count > 0
              ? <>oldest from <strong>{pr.oldestDate}</strong> — <Link to="/review">open Review</Link></>
              : 'inbox is clear'}
          </div>
        </Card>

        <Card title="🚛 Outgoing loads" tone={(() => {
          const c = staleColor(ol.lastImportedAt, 24 * 3, 24 * 7);
          return c === '#991b1b' ? 'bad' : c === '#c05100' ? 'warn' : undefined;
        })()}>
          <div className="small">Last import: <strong style={{ color: staleColor(ol.lastImportedAt, 24 * 3, 24 * 7) }}>{ago(ol.lastImportedAt)}</strong></div>
          <div className="small">Latest load date: <strong>{ol.lastLoadDate || '—'}</strong></div>
          <div className="small">Rows in last 7 days: <strong>{ol.rowsLast7Days ?? 0}</strong> — <Link to="/outgoing-loads">open tab</Link></div>
        </Card>

        <Card title="📄 Google sheet sync" tone={(() => {
          const c = staleColor(ss.lastEntryAt, 24, 72);
          return c === '#991b1b' ? 'bad' : c === '#c05100' ? 'warn' : undefined;
        })()}>
          <div className="small">Newest form entry in DB: <strong style={{ color: staleColor(ss.lastEntryAt, 24, 72) }}>{ago(ss.lastEntryAt)}</strong></div>
          <div className="small" style={{ color: '#666' }}>
            If this trails the actual sheet, check the Apps Script trigger + WARMUP_TOKEN.
          </div>
        </Card>
      </div>

      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', border: invMissed > 0 ? '1px solid #fca5a5' : '1px solid #e2e8f0', background: invMissed > 0 ? '#fef2f2' : '#fff' }}>
        <div style={{ fontWeight: 700, marginBottom: '0.2rem' }}>
          🧾 Invoicing reminders {invMissed > 0 ? '— ' + invMissed + ' behind schedule' : '— all on track ✓'}
        </div>
        {inv.error && <div className="small" style={{ color: '#991b1b' }}>Check failed: {inv.error}</div>}
        {!inv.error && (
          <>
            {invStmt.length + invWeekly.length === 0 && (
              <div className="small" style={{ color: '#15803d', marginBottom: '0.5rem' }}>
                All scheduled groups are either invoiced on time or have nothing outstanding. ✓
              </div>
            )}
            {invStmt.length + invWeekly.length > 0 && (
            <table className="fs-table tidy" style={{ fontSize: '0.9em', marginBottom: '0.6rem' }}>
              <thead><tr><th>Customer group</th><th>Cadence</th><th>Last QBO invoice</th><th className="num">Open balance</th><th>Status</th></tr></thead>
              <tbody>
                {invStmt.map(g => (
                  <tr key={g.label} style={g.status === 'overdue' ? { background: '#fee2e2' } : (g.status === 'nothing-to-bill' ? { opacity: 0.55 } : undefined)}>
                    <td>{g.label}</td>
                    <td className="small">with each statement</td>
                    <td>{g.lastInvoiceDate || 'none in lookback'}</td>
                    <td className="num">{g.openBalance == null ? 'n/a' : (g.openBalance < 0 ? money(g.openBalance) : '—')}</td>
                    <td style={{ fontWeight: 600, color: g.status === 'overdue' ? '#b91c1c' : g.status === 'on-track' ? '#15803d' : '#64748b' }}>
                      {g.status === 'on-track' ? 'on track' : g.status === 'overdue' ? '⚠ not invoiced this cycle' : 'nothing to bill'}
                    </td>
                  </tr>
                ))}
                {invWeekly.map(g => (
                  <tr key={g.label} style={g.status === 'overdue' ? { background: '#fee2e2' } : (g.status === 'nothing-to-bill' ? { opacity: 0.55 } : undefined)}>
                    <td>{g.label}</td>
                    <td className="small">weekly</td>
                    <td>{g.lastInvoiceDate ? g.lastInvoiceDate + ' (' + g.daysSince + 'd ago)' : 'none in lookback'}</td>
                    <td className="num">{g.openBalance == null ? 'n/a' : (g.openBalance < 0 ? money(g.openBalance) : '—')}</td>
                    <td style={{ fontWeight: 600, color: g.status === 'overdue' ? '#b91c1c' : g.status === 'on-track' ? '#15803d' : '#64748b' }}>
                      {g.status === 'on-track' ? 'on track' : g.status === 'overdue' ? '⚠ overdue' : 'nothing to bill'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
            <div className="small" style={{ color: '#666', marginBottom: '0.4rem' }}>
              Rules: weekly = {inv.weeklyRule}; statement = {inv.statementRule}. Matched against QBO invoice customer names. Overdue requires an OPEN balance — groups with nothing owed show grey.
            </div>
            <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>
              Other customers with a negative balance — bill them:
            </div>
            {invNeg.length === 0 && <div className="small" style={{ color: '#15803d' }}>None. ✓</div>}
            {invNeg.length > 0 && (
              <table className="fs-table tidy" style={{ fontSize: '0.9em' }}>
                <thead><tr><th>Customer</th><th className="num">Current balance</th></tr></thead>
                <tbody>
                  {invNeg.map(n => (
                    <tr key={n.customer}>
                      <td><Link to="/balances">{n.customer}</Link></td>
                      <td className="num" style={{ color: '#b91c1c', fontWeight: 600 }}>{money(n.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      <div className="card" style={{ padding: '0.75rem 1rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '0.2rem' }}>
          💰 Balances below historical average — cycle {ba.cycleKey}
        </div>
        <div className="small" style={{ color: '#666', marginBottom: '0.5rem' }}>Rule: {ba.rule}</div>
        {alerts.length === 0 && (
          <div className="small" style={{ color: '#15803d' }}>No customers above threshold. ✓</div>
        )}
        {alerts.length > 0 && (
          <table className="fs-table tidy" style={{ fontSize: '0.9em' }}>
            <thead><tr>
              <th>Customer</th>
              <th className="num">Current balance</th>
              <th className="num">Avg closing ({'last ' + (alerts[0].cyclesInAvg || 6) + ' cycles'})</th>
              <th className="num">Under by</th>
            </tr></thead>
            <tbody>
              {alerts.map(a => (
                <tr key={a.customer}>
                  <td><Link to="/balances">{a.customer}</Link></td>
                  <td className="num" style={{ fontWeight: 600 }}>{money(a.current)}</td>
                  <td className="num">{money(a.avgClosing)}</td>
                  <td className="num" style={{ color: a.pctUnder >= 75 ? '#991b1b' : '#c05100', fontWeight: 700 }}>
                    −{a.pctUnder}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
